import React, { useState, useEffect } from 'react';
import { FishingArea } from '../entities/FishingEntities';
import { updateAreaName, deleteArea } from '../services/FishingService';
import { db } from '../services/FishingDB';

interface Props {
  area: FishingArea | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
  onSuccess: (message: string, type?: 'info' | 'success' | 'error') => void;
}

export const AreaDetailModal: React.FC<Props> = ({ 
  area, 
  isOpen, 
  onClose, 
  onUpdate,
  onSuccess 
}) => {
  const [name, setName] = useState('');
  const [catchCount, setCatchCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const loadCatchCount = async () => {
    if (!area) return;
    const catches = await db.catches
      .where('fishingAreaId')
      .equals(area.id)
      .toArray();
    setCatchCount(catches.length);
  };

  useEffect(() => {
    if (area) {
      setName(area.name);
      loadCatchCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [area]);

  if (!isOpen || !area) return null;

  const handleSave = async () => {
    if (!name.trim()) {
      onSuccess('❌ Namn kan inte vara tomt', 'error');
      return;
    }

    setIsLoading(true);
    try {
      await updateAreaName(area.id, name);
      onSuccess('✅ Område uppdaterat!', 'success');
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating area:', error);
      onSuccess('❌ Kunde inte uppdatera område', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      await deleteArea(area.id);
      onSuccess('🗑️ Område borttaget', 'info');
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error deleting area:', error);
      onSuccess('❌ Kunde inte ta bort område', 'error');
    } finally {
      setIsLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h2>📍 Redigera Område</h2>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <div style={styles.content}>
          {/* Name */}
          <div style={styles.field}>
            <label style={styles.label}>Områdets namn</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="T.ex. Min favoritplats"
              style={styles.input}
              autoFocus
            />
          </div>

          {/* Stats */}
          <div style={styles.statsGrid}>
            <div style={styles.statBox}>
              <div style={styles.statValue}>{catchCount}</div>
              <div style={styles.statLabel}>Fångster</div>
            </div>
            <div style={styles.statBox}>
              <div style={styles.statValue}>{area.radius}m</div>
              <div style={styles.statLabel}>Radie</div>
            </div>
          </div>

          {/* Location info */}
          <div style={styles.infoBox}>
            📍 Center: {area.centerLat.toFixed(5)}, {area.centerLng.toFixed(5)}
          </div>

          <div style={styles.infoBox}>
            🕒 Senast besökt: {new Date(area.lastVisited).toLocaleString('sv-SE')}
          </div>

          {/* Actions */}
          <div style={styles.actions}>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={styles.deleteButton}
              disabled={isLoading}
            >
              🗑️ Ta bort
            </button>
            <button
              onClick={handleSave}
              style={styles.saveButton}
              disabled={isLoading || !name.trim()}
            >
              {isLoading ? 'Sparar...' : '💾 Spara'}
            </button>
          </div>

          <div style={styles.helpText}>
            💡 När du tar bort ett område flyttas fångsterna till "Okänd plats"
          </div>
        </div>

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div style={styles.confirmOverlay}>
            <div style={styles.confirmBox}>
              <h3>Ta bort område?</h3>
              <p>Fångsterna ({catchCount} st) flyttas till "Okänd plats".</p>
              <div style={styles.confirmActions}>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  style={styles.cancelButton}
                >
                  Avbryt
                </button>
                <button
                  onClick={handleDelete}
                  style={styles.confirmDeleteButton}
                  disabled={isLoading}
                >
                  {isLoading ? 'Tar bort...' : 'Ta bort'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '85vh',
    overflow: 'auto',
    position: 'relative' as const,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e0e0e0',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#666',
  },
  content: {
    padding: '20px',
  },
  field: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontWeight: '500',
    marginBottom: '8px',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '12px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px',
    boxSizing: 'border-box' as const,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    marginBottom: '20px',
  },
  statBox: {
    backgroundColor: '#f5f5f5',
    padding: '16px',
    borderRadius: '8px',
    textAlign: 'center' as const,
  },
  statValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#007AFF',
    marginBottom: '4px',
  },
  statLabel: {
    fontSize: '14px',
    color: '#666',
  },
  infoBox: {
    backgroundColor: '#f5f5f5',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '12px',
    fontSize: '14px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
  },
  deleteButton: {
    flex: 1,
    padding: '16px',
    border: '2px solid #dc3545',
    backgroundColor: 'white',
    color: '#dc3545',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
  },
  saveButton: {
    flex: 2,
    padding: '16px',
    border: '2px solid #007AFF',
    backgroundColor: '#007AFF',
    color: 'white',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
  },
  helpText: {
    fontSize: '13px',
    color: '#666',
    marginTop: '16px',
    textAlign: 'center' as const,
  },
  confirmOverlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '12px',
  },
  confirmBox: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '300px',
    textAlign: 'center' as const,
  },
  confirmActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '20px',
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    border: '2px solid #e0e0e0',
    backgroundColor: 'white',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  confirmDeleteButton: {
    flex: 1,
    padding: '12px',
    border: '2px solid #dc3545',
    backgroundColor: '#dc3545',
    color: 'white',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '500',
  },
};

export default AreaDetailModal;
